import React, { useEffect, useRef, useState } from "react";
import { useNavigate } from "react-router-dom";
import { Sparkles, ArrowRight, ArrowLeft } from "lucide-react";
import { toast } from "sonner";
import { Dialog, DialogContent, DialogTitle, DialogDescription } from "./ui/dialog";
import { Input } from "./ui/input";
import { Button } from "./ui/button";
import { api } from "../lib/api";
import { useAuth } from "../lib/auth";
import { log } from "../lib/log";

const STEPS = [
  { id: "name", q: "What's your name?", hint: "How your blueprint will greet you.", type: "text", placeholder: "Aurora Vale", auto: "name" },
  { id: "birth_date", q: "When were you born?", hint: "Your Sun sign, Chinese zodiac and life path come from this.", type: "date" },
  { id: "birth_time", q: "What time were you born?", hint: "Even an estimate helps place your Moon and Rising sign.", type: "time" },
  { id: "birth_location", q: "Where were you born?", hint: "City and country — this anchors your lines on the map.", type: "text", placeholder: "Lisbon, Portugal" },
  { id: "email", q: "Where should we send your free healing PDFs?", hint: "No password. The 8-Day Healing Blueprint + Destination Wellness Chapter One go here.", type: "email", placeholder: "you@example.com", auto: "email" },
  { id: "referred_by", q: "Did someone recommend StarGrid?", hint: "Optional — the name of the person, healer or business who sent you. Leave blank to skip.", type: "text", placeholder: "e.g. Luna Wellness Studio", optional: true },
];

// Gamified one-question-at-a-time start: Name -> Date -> Time -> Place -> Email -> dashboard.
export default function GuestStartModal({ open, onOpenChange, onSignIn }) {
  const navigate = useNavigate();
  const { refresh } = useAuth();
  const [step, setStep] = useState(0);
  const [form, setForm] = useState({ name: "", email: "", birth_date: "", birth_time: "", birth_location: "", referred_by: "" });
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState("");
  const inputRef = useRef(null);
  const cur = STEPS[step];
  const last = step === STEPS.length - 1;
  const value = form[cur.id];
  const valid = cur.optional || (cur.type === "email" ? /^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(value) : value.trim().length >= (cur.type === "text" ? 2 : 1));

  useEffect(() => { if (open) setTimeout(() => inputRef.current?.focus(), 80); }, [step, open]);

  const next = async (e) => {
    e?.preventDefault();
    if (!valid) return;
    setError("");
    if (!last) { setStep((s) => s + 1); return; }
    setBusy(true);
    try {
      await api.post("/auth/guest", form);
      await refresh();
      toast.success("Your blueprint is ready ✨ Two free healing PDFs are on their way to your inbox.");
      navigate("/dashboard", { replace: true });
    } catch (err) {
      const detail = err?.response?.data?.detail;
      setError(typeof detail === "string" ? detail : "Please check your details and try again.");
      log.warn("guest start failed:", err);
    } finally {
      setBusy(false);
    }
  };

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="max-w-md bg-[#15111A] border border-[#C8A96E]/40 text-white p-0 overflow-hidden" data-testid="guest-start-modal">
        <div className="h-1.5 bg-white/10">
          <div className="h-full bg-[#C8A96E] transition-[width] duration-500" style={{ width: `${((step + 1) / STEPS.length) * 100}%` }} data-testid="guest-progress" />
        </div>
        <form onSubmit={next} className="p-6 sm:p-8" data-testid="guest-start-form">
          <div className="text-[10px] uppercase tracking-[0.3em] text-[#C8A96E] flex items-center gap-2">
            <Sparkles size={12} /> Step {step + 1} of {STEPS.length}
          </div>
          <DialogTitle className="font-heading text-2xl sm:text-3xl mt-3 leading-tight" data-testid="guest-step-question">{cur.q}</DialogTitle>
          <DialogDescription className="text-[#A89C98] text-sm mt-1">{cur.hint}</DialogDescription>
          <Input
            key={cur.id}
            ref={inputRef}
            id={`guest-${cur.id}`}
            data-testid={`guest-input-${cur.id}`}
            type={cur.type}
            required={!cur.optional}
            autoComplete={cur.auto}
            placeholder={cur.placeholder}
            value={value}
            onChange={(e) => setForm((f) => ({ ...f, [cur.id]: e.target.value }))}
            className="mt-6 bg-white/5 border-white/15 text-white placeholder:text-[#6b5a86] h-14 text-lg rounded-none focus-visible:ring-[#C8A96E]"
          />
          {error && <p role="alert" data-testid="guest-start-error" className="mt-3 text-sm text-[#FF6B8A]">{error}</p>}
          <div className="mt-6 flex items-center gap-3">
            {step > 0 && (
              <button type="button" onClick={() => setStep((s) => s - 1)} data-testid="guest-back-btn" className="inline-flex items-center gap-1 h-12 px-4 text-[11px] uppercase tracking-widest text-[#A89C98] hover:text-white transition-colors">
                <ArrowLeft size={14} /> Back
              </button>
            )}
            <Button
              type="submit"
              disabled={busy || !valid}
              data-testid="guest-start-submit"
              className="flex-1 h-12 bg-[#C8A96E] text-[#15111A] hover:bg-[#A98B55] rounded-none uppercase tracking-widest text-xs font-bold shadow-[0_0_30px_rgba(200,169,110,0.35)] disabled:opacity-40"
            >
              {busy ? "Mapping your sky…" : last ? <>Reveal my blueprint <ArrowRight size={14} className="ml-2" /></> : <>Continue <ArrowRight size={14} className="ml-2" /></>}
            </Button>
          </div>
          <p className="mt-4 text-[11px] text-[#A89C98] text-center">
            Already have an account?{" "}
            <button type="button" onClick={onSignIn} data-testid="guest-start-signin" className="text-[#C8A96E] hover:text-white underline underline-offset-2">Sign in with Google</button>
          </p>
        </form>
      </DialogContent>
    </Dialog>
  );
}
